import React, { useEffect } from 'react'

/**
 * Scroll reveal for page content blocks.
 *
 * Each direct child of the active `.page` is treated as one logical block
 * (the header, a section, a table, a card grid…). The first time a block
 * appears we look at where it sits:
 *   - already within the viewport (above the fold) → shown immediately, with no
 *     entrance animation;
 *   - below the fold → hidden, then it fades up (the `wfade` keyframe) the
 *     moment it scrolls into view.
 *
 * So a fresh page never animates the content the user is already looking at —
 * only the blocks they scroll down to. It runs app-wide with zero per-view
 * markup: a MutationObserver watches <main> so blocks that appear after an async
 * data fetch are handled too, and a single IntersectionObserver drives the
 * reveal. Honors prefers-reduced-motion (then nothing is hidden or animated).
 */
const ScrollReveal: React.FC = () => {
  useEffect(() => {
    const main = document.querySelector('main')
    if (!main) return

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const seen = new WeakSet<Element>()

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const el = entry.target as HTMLElement
          io.unobserve(el)
          el.classList.remove('reveal-pending')
          el.classList.add('reveal-show')
          // Drop the class once the animation ends so no lingering transform is
          // left on the block — a persisted transform would become the
          // containing block for any position:fixed/sticky descendant.
          el.addEventListener('animationend', () => el.classList.remove('reveal-show'), { once: true })
        }
      },
      // Trigger just before a block is fully in view for a smoother entrance.
      { rootMargin: '0px 0px -8% 0px' },
    )

    let queued = false
    const scan = () => {
      queued = false
      const page = main.querySelector('.page')
      if (!page) return
      for (const el of Array.from(page.children)) {
        if (seen.has(el)) continue
        seen.add(el)
        if (reduceMotion) continue
        const belowFold = el.getBoundingClientRect().top >= window.innerHeight
        if (belowFold) {
          el.classList.add('reveal-pending')
          io.observe(el)
        }
        // Above/at the fold: leave it untouched — visible immediately.
      }
    }
    const schedule = () => {
      if (queued) return
      queued = true
      requestAnimationFrame(scan)
    }

    const mo = new MutationObserver(schedule)
    mo.observe(main, { childList: true, subtree: true })
    scan()

    return () => {
      mo.disconnect()
      io.disconnect()
    }
  }, [])

  return null
}

export default ScrollReveal
